import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { brandVars } from "@/lib/brand";
import { Mic, Upload, CheckCircle2, AlertTriangle, Send } from "lucide-react";

const STORY_HELPERS = {
  q1: "Why we ask: a strong opinion makes a better headline than a summary.",
  q2: "Why we ask: specifics stop the scroll. \"I ran my first marathon at 55\" beats \"I love running\".",
};

function formatWording(show_format) {
  const isLive = show_format === "live_video";
  return {
    isLive,
    thingLabel: isLive ? "live show" : "episode",
    dateLabel: isLive ? "Live date" : "Recording date",
  };
}

export default function IntakeForm() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [form, setForm] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get(`/intake/${token}`)
      .then(r => { setData(r.data); setForm(r.data.guest); })
      .catch(() => setNotFound(true));
  }, [token]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!(form.full_name || "").trim() || !(form.email || "").trim()) {
      setError("Please fill in your name and email.");
      return;
    }
    if (!(form.story_one_sentence_intro || "").trim()) {
      setError("Please answer the one-sentence intro — this is how we introduce you.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        full_name: form.full_name,
        email: form.email,
        phone: form.phone || "",
        whatsapp: form.whatsapp || "",
        story_one_sentence_intro: form.story_one_sentence_intro || "",
        website: form.website || "",
        linkedin_url: form.linkedin_url || "",
        facebook: form.facebook || "",
        instagram: form.instagram || "",
        youtube: form.youtube || "",
        story_answers: form.story_answers || {},
      };
      await api.put(`/intake/${token}`, payload);
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong — please try again");
    } finally { setSubmitting(false); }
  };

  if (notFound) {
    return (
      <PublicShell>
        <div className="pgm-card" style={{ textAlign: "center", padding: 40 }} data-testid="intake-not-found">
          <AlertTriangle size={28} color="var(--pgm-orange)" style={{ margin: "0 auto 12px" }} />
          <h2 className="pgm-display" style={{ fontSize: 26 }}>Form not found</h2>
          <p style={{ opacity: 0.75 }}>This link doesn&rsquo;t match any guest. Please check the link in your email, or contact the show host.</p>
        </div>
      </PublicShell>
    );
  }
  if (!data) return <PublicShell><div style={{ textAlign: "center", padding: 40 }}>Loading…</div></PublicShell>;

  const logoSrc = data.show.podcast_logo_url
    ? (data.show.podcast_logo_url.startsWith("/api") ? `${BACKEND_URL}${data.show.podcast_logo_url}` : data.show.podcast_logo_url)
    : null;
  const wording = formatWording(data.show.show_format);
  const questions = Array.isArray(data.show.intake_questions) && data.show.intake_questions.length > 0
    ? data.show.intake_questions
    : [];
  const answers = form.story_answers || {};
  const setAnswer = (qid, text) => set("story_answers", { ...(form.story_answers || {}), [qid]: text });

  const inputField = (key, label, opts = {}) => (
    <div style={opts.full ? { gridColumn: "1 / -1" } : undefined}>
      <label className="pgm-label">{label}{opts.required && " *"}</label>
      <input
        className="pgm-input"
        type={opts.type || "text"}
        placeholder={opts.placeholder || ""}
        value={form[key] || ""}
        onChange={(e) => set(key, e.target.value)}
        data-testid={`intake-${key}`}
      />
    </div>
  );

  return (
    <PublicShell brand={data.show.brand_colors}>
      <div style={{ textAlign: "center", marginBottom: 26 }}>
        {logoSrc ? (
          <img src={logoSrc} alt="show logo" style={{ width: 72, height: 72, objectFit: "cover", border: "2px solid var(--pgm-magenta)", margin: "0 auto 12px", display: "block" }} data-testid="intake-show-logo" />
        ) : (
          <div style={{ width: 72, height: 72, background: "var(--pgm-magenta)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <Mic size={30} color="var(--pgm-cream)" />
          </div>
        )}
        <div className="pgm-micro">◆ Guest intake form</div>
        <h1 className="pgm-display" style={{ fontSize: 36, lineHeight: 1.05 }} data-testid="intake-show-name">{data.show.show_name}</h1>
        {data.episode_date && (
          <p style={{ opacity: 0.7, marginTop: 6 }} data-testid="intake-date-line">
            {wording.dateLabel}: {data.episode_date}
          </p>
        )}
      </div>

      {data.show.intake_welcome && (
        <div className="pgm-card" style={{ marginBottom: 18, borderLeftColor: "var(--pgm-magenta)" }} data-testid="intake-welcome">
          <p style={{ fontSize: 15, lineHeight: 1.6, margin: 0, whiteSpace: "pre-wrap" }}>{data.show.intake_welcome}</p>
        </div>
      )}

      {(submitted || data.form_complete) && (
        <div className="pgm-callout" style={{ background: "var(--pgm-olive)", color: "var(--pgm-cream)", marginBottom: 18 }} data-testid="intake-complete-banner">
          <CheckCircle2 size={15} style={{ verticalAlign: "middle", marginRight: 8 }} />
          <strong>{submitted ? "Thank you! Your info has been submitted." : "You've already submitted this form."}</strong> You can come back to this link anytime to update your answers.
        </div>
      )}

      <form onSubmit={submit} className="pgm-card" style={{ padding: 28 }}>
        <HeadshotUploader token={token} value={form.headshot_url} onChange={(url) => set("headshot_url", url)} />

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 18 }}>
          {inputField("full_name", "Full name", { required: true })}
          {inputField("email", "Email address", { type: "email", required: true })}
          {inputField("phone", "Mobile number (optional — only used if we need to reach you on recording day)", { type: "tel", full: true, placeholder: "+1 555 123 4567" })}
          {inputField("whatsapp", "WhatsApp number (optional — handy if you're outside North America)", { type: "tel", full: true, placeholder: "+1 555 123 4567" })}

          <div style={{ gridColumn: "1 / -1" }}>
            <label className="pgm-label">How would you introduce yourself, in one sentence? *</label>
            <textarea
              className="pgm-textarea"
              placeholder="What you do, or the story you're here to share. Example: &quot;I help families navigate elder care&quot; or &quot;I walked away from a 25-year marriage at 60 and started over&quot;."
              value={form.story_one_sentence_intro || ""}
              onChange={(e) => set("story_one_sentence_intro", e.target.value)}
              data-testid="intake-story_one_sentence_intro"
            />
            <div style={{ fontSize: 12, opacity: 0.65, marginTop: 4 }}>
              This becomes how we introduce you in the {wording.thingLabel} promo.
            </div>
          </div>
        </div>

        <div style={{ marginTop: 22 }} data-testid="intake-links-section">
          <div className="pgm-micro">◆ Your links</div>
          <p style={{ fontSize: 12, opacity: 0.65, margin: "2px 0 10px 0" }}>
            Optional. We&rsquo;ll pull bio and story details from anything public you share.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            {inputField("website", "Website URL", { placeholder: "https://" })}
            {inputField("linkedin_url", "LinkedIn profile URL", { placeholder: "https://linkedin.com/in/..." })}
            {inputField("facebook", "Facebook URL")}
            {inputField("instagram", "Instagram handle")}
            {inputField("youtube", "YouTube channel URL", { full: true })}
          </div>
        </div>

        <div style={{ marginTop: 28, paddingTop: 22, borderTop: "1px solid var(--pgm-rule)" }} data-testid="intake-story-section">
          <div className="pgm-micro">◆ Story Material</div>
          <h2 className="pgm-display" style={{ fontSize: 24, marginTop: 4 }}>
            {questions.length === 1 ? "One quick question" : `${questions.length} quick questions`} (all optional)
          </h2>
          <p style={{ fontSize: 14, opacity: 0.8, marginTop: 6, marginBottom: 18 }}>
            These questions help us write promo copy people actually click on. A sentence or two each is plenty. Skip anything that doesn&rsquo;t fit.
          </p>
          <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 18 }}>
            {questions.map((q, i) => (
              <div key={q.id}>
                <label className="pgm-label">{q.label}</label>
                <textarea
                  className="pgm-textarea"
                  value={answers[q.id] || ""}
                  onChange={(e) => setAnswer(q.id, e.target.value)}
                  data-testid={`intake-story-answer-${q.id}`}
                />
                {STORY_HELPERS[q.id] && (
                  <div style={{ fontSize: 12, opacity: 0.65, marginTop: 4 }}>{STORY_HELPERS[q.id]}</div>
                )}
              </div>
            ))}
          </div>
        </div>

        {error && (
          <div className="pgm-callout" style={{ borderLeftColor: "var(--pgm-orange)", marginTop: 16 }} data-testid="intake-error">
            <AlertTriangle size={14} style={{ verticalAlign: "middle", marginRight: 6 }} /> {error}
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
          <button className="pgm-btn" type="submit" disabled={submitting} data-testid="intake-submit">
            <Send size={14} /> {submitting ? "Submitting…" : (submitted || data.form_complete) ? "Update my answers" : "Submit"}
          </button>
        </div>
      </form>

      <p style={{ textAlign: "center", fontSize: 12, opacity: 0.55, marginTop: 20 }}>
        Powered by ShowRunner HQ
      </p>
    </PublicShell>
  );
}

function PublicShell({ children, brand }) {
  return (
    <div style={{ minHeight: "100vh", background: "var(--pgm-cream)", padding: "48px 20px", ...brandVars(brand) }}>
      <div style={{ maxWidth: 720, margin: "0 auto" }}>{children}</div>
    </div>
  );
}

function HeadshotUploader({ token, value, onChange }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [err, setErr] = useState("");
  const src = value?.startsWith("/api") ? `${BACKEND_URL}${value}` : value;

  const onPick = useCallback(async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true); setErr("");
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/intake/${token}/headshot`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
    } catch (error) {
      setErr(error?.response?.data?.detail || "Upload failed — please try again");
    } finally { setUploading(false); }
  }, [token, onChange]);

  return (
    <div style={{ display: "flex", gap: 18, alignItems: "center" }}>
      <div style={{ width: 96, height: 96, border: "1.5px solid var(--pgm-rule)", background: "var(--pgm-cream)", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
        {src ? <img src={src} alt="headshot" style={{ width: "100%", height: "100%", objectFit: "cover" }} data-testid="intake-headshot-preview" /> : <span style={{ fontSize: 10, opacity: 0.5, textAlign: "center" }}>No photo yet</span>}
      </div>
      <div>
        <label className="pgm-label">Headshot photo — square, at least 800×800</label>
        <input ref={fileRef} type="file" accept="image/*" onChange={onPick} style={{ display: "none" }} data-testid="intake-headshot-input" />
        <button type="button" className="pgm-btn pgm-btn-ghost" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="intake-headshot-btn">
          <Upload size={13} /> {uploading ? "Uploading…" : value ? "Replace photo" : "Upload photo"}
        </button>
        {err && <div style={{ color: "var(--pgm-orange)", fontSize: 12, marginTop: 6 }}>{err}</div>}
      </div>
    </div>
  );
}
